import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../i18n/LangContext";
import { Field, inputProps } from "./Field";

const useSuggestions = (query, enabled) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled || (query || "").trim().length < 5) {
      setItems([]);
      return undefined;
    }
    const h = setTimeout(() => {
      api.get("/public/geocode/search", { params: { q: query } }).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, 700);
    return () => clearTimeout(h);
  }, [query, enabled]);
  return [items, setItems];
};

export const AddressFields = ({ prefix, value, onChange, errors, mapsEnabled, notesLabel, notesPh }) => {
  const { t } = useLang();
  const [typing, setTyping] = useState(false);
  const [items, setItems] = useSuggestions(typing ? value.street : "", mapsEnabled && typing);
  const blurTimer = useRef(null);
  const err = (k) => errors[`${prefix}.${k}`];

  const pick = (s) => {
    onChange({ street: s.street || value.street, city: s.city || value.city, state: s.state || value.state, zip: s.zip || value.zip, lat: s.lat, lng: s.lng });
    setTyping(false);
    setItems([]);
  };

  return (
    <div className="grid gap-4 sm:grid-cols-6">
      <Field id={`${prefix}-street`} label={t("wizard.street")} error={err("street")} required className="relative sm:col-span-6">
        <input
          {...inputProps(`${prefix}-street`, err("street"))}
          value={value.street}
          autoComplete="street-address"
          placeholder={t("wizard.streetPh")}
          role={mapsEnabled ? "combobox" : undefined}
          aria-expanded={items.length > 0}
          aria-controls={`${prefix}-suggestions`}
          onChange={(e) => { setTyping(true); onChange({ street: e.target.value, lat: null, lng: null }); }}
          onBlur={() => { blurTimer.current = setTimeout(() => setItems([]), 200); }}
        />
        {items.length > 0 && (
          <ul id={`${prefix}-suggestions`} role="listbox" aria-label={t("wizard.suggestions")} className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-[#3F3F4E] bg-[#121216] shadow-2xl" data-testid={`${prefix}-suggestions`}>
            {items.map((s, i) => (
              <li key={`${s.lat}-${s.lng}-${i}`} role="option" aria-selected="false">
                <button type="button" className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm text-gray-200 hover:bg-white/5 focus:bg-white/5" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)} data-testid={`${prefix}-suggestion-${i}`}>
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#DC2626]" aria-hidden="true" /> {s.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>
      <Field id={`${prefix}-city`} label={t("wizard.city")} error={err("city")} required className="sm:col-span-3">
        <input {...inputProps(`${prefix}-city`, err("city"))} value={value.city} autoComplete="address-level2" onChange={(e) => onChange({ city: e.target.value })} />
      </Field>
      <Field id={`${prefix}-state`} label={t("wizard.state")} error={err("state")} required className="sm:col-span-1">
        <input {...inputProps(`${prefix}-state`, err("state"))} value={value.state} maxLength={30} autoComplete="address-level1" onChange={(e) => onChange({ state: e.target.value.toUpperCase() })} />
      </Field>
      <Field id={`${prefix}-zip`} label={t("wizard.zip")} error={err("zip")} required className="sm:col-span-2">
        <input {...inputProps(`${prefix}-zip`, err("zip"))} value={value.zip} inputMode="numeric" maxLength={10} autoComplete="postal-code" onChange={(e) => onChange({ zip: e.target.value.replace(/[^\d-]/g, "") })} />
      </Field>
      <Field id={`${prefix}-notes`} label={notesLabel} className="sm:col-span-6">
        <textarea {...inputProps(`${prefix}-notes`)} rows={3} maxLength={600} value={value.notes} placeholder={notesPh} onChange={(e) => onChange({ notes: e.target.value })} />
      </Field>
    </div>
  );
};
